import React from 'react';
import { Box, Chip, Stack, Typography, type TypographyProps } from '@mui/material';
import { BRAND_NAME_LINE_2, BRAND_TAGLINE } from '../constants/brand';
import { BASELINE_SOFTWARE_VERSION } from '../constants/version';

export type BrandProductNameSize = 'compact' | 'default' | 'large';

export interface BrandProductNameProps {
  size?: BrandProductNameSize;
  align?: TypographyProps['align'];
  /** When true, shows the Measure · Report · Reduce tagline below the product name. */
  showTagline?: boolean;
  /** When true, shows the baseline software version below the tagline (or product name). */
  showVersion?: boolean;
}

const SIZE_STYLES: Record<
  BrandProductNameSize,
  { line1: TypographyProps['variant']; line2: TypographyProps['variant']; line1Size?: string; line2Size?: string }
> = {
  compact: {
    line1: 'subtitle2',
    line2: 'subtitle1',
    line1Size: '0.8rem',
    line2Size: '0.95rem',
  },
  default: {
    line1: 'h6',
    line2: 'h5',
  },
  large: {
    line1: 'h5',
    line2: 'h4',
    line1Size: '1.35rem',
    line2Size: '1.65rem',
  },
};

/**
 * Two-line product name aligned with the Sustainow launch logo lockup.
 * DEEP-BUG-001: keep each brand line as a single text node (no nested colored
 * spans / negative tracking) so glyphs do not paint twice.
 */
const versionChipSx = {
  height: 20,
  '& .MuiChip-label': {
    px: 1,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: 0.2,
  },
} as const;

const BrandProductName: React.FC<BrandProductNameProps> = ({
  size = 'default',
  align = 'left',
  showTagline = false,
  showVersion = false,
}) => {
  const styles = SIZE_STYLES[size];

  return (
    <Box sx={{ textAlign: align, minWidth: 0 }}>
      <Typography
        variant={styles.line1}
        component="div"
        sx={{
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: 0,
          textTransform: 'lowercase',
          color: 'primary.main',
          fontSize: styles.line1Size,
          textShadow: 'none',
          WebkitTextStroke: 0,
        }}
      >
        sustainow
      </Typography>
      <Typography
        variant={styles.line2}
        component="div"
        sx={{
          fontWeight: 700,
          lineHeight: 1.15,
          letterSpacing: 0,
          color: 'primary.dark',
          fontSize: styles.line2Size,
          textShadow: 'none',
          WebkitTextStroke: 0,
        }}
      >
        {BRAND_NAME_LINE_2}
      </Typography>
      {showTagline && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            lineHeight: 1.2,
            mt: 0.25,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {BRAND_TAGLINE}
        </Typography>
      )}
      {showVersion && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: showTagline ? 0.5 : 0.25, display: { xs: 'none', sm: 'flex' } }}
        >
          <Chip
            size="small"
            label={`Version ${BASELINE_SOFTWARE_VERSION}`}
            sx={versionChipSx}
            variant="outlined"
            color="default"
          />
        </Stack>
      )}
    </Box>
  );
};

export default BrandProductName;
